import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { ExcelService } from '../../../services/excel.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadChangeParam, NzUploadFile } from 'ng-zorro-antd/upload';
import { Router } from '@angular/router';
import { CartService } from '../../../services/cart.service';
import { Product } from '../../../models/product.model';
import { CommonService } from 'src/app/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { APP_BASE_HREF } from '@angular/common';

@Component({
  selector: 'app-csv-excel',
  templateUrl: './csv-excel.component.html',
  styleUrls: ['./csv-excel.component.less'],
})
export class CsvExcelComponent implements OnInit {
  @Input() isModal: boolean = false;
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
  @Output() canAdd: EventEmitter<boolean> = new EventEmitter<boolean>();
  isUploaded: boolean = false;
  fileList:NzUploadFile[]=[];
  file!: NzUploadFile;
  fileSize:number=0;
  result: { isValid: boolean; data?: any[]; errors?: string[] } = {
    isValid: false,
    data: [],
    errors: [],
  };
  constructor(
    private excel: ExcelService,
    private message: NzMessageService,
    private router: Router,
    private cartService: CartService,
    private commonService:CommonService,
    private translate: TranslateService,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {}

  ngOnInit(): void {}
  isValid(): boolean {
    return false;
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    //Preprocess files here, such as verifying file types, etc
    const type = file.name.split('.').pop();
    if (type !== 'xls' && type !== 'xlsx' && type !== 'csv') {
      this.message.error(this.translate.instant('UPLOAD_FILE_TIPS'));
      this.isUploaded = false;
      return false;
    }
    // Return true to continue uploading, return false to cancel uploading
    return true;
  };
  removeFile = (file?: NzUploadFile): boolean =>{
    this.fileList = [];
    this.canAdd.emit(true);
    this.isUploaded=false;
    return true;
  }
  async importFile(e: NzUploadChangeParam): Promise<void> {
    //this.fileList = [...event];
    let fileList = [...e.fileList];
    this.fileList = fileList.slice(-1);
    this.file = e.file;
    const file = e.file;
    this.fileSize = file.size ? Math.max(1, parseInt((file.size / 1024).toFixed(0))) : 0;
    const status = file.status; 
    if (status === 'done' && file.originFileObj) {
      this.isUploaded = true;
      //Reading files
      let result = await this.excel.read(file.originFileObj, {
        header: ['materialNumber', 'quantity', 'expectedDate', 'nonBoschPartNoFlag'],
      });
      this.result = this.validateAndProcessData(result);
      const canAdd = !this.result.isValid || !this.isUploaded;
      this.canAdd.emit(canAdd);
    } else if (status === 'error') {
      console.error('Upload Error');
      this.isUploaded = false;
    }
  }

  addToCart(): void {
    const products: Product[] = [];
    this.result?.data?.forEach((control: any) => {
      let productNumber = control.materialNumber;
      if (productNumber) {
        const dateValue: Date | null = control.expectedDate || new Date();
        const formattedDate: string = dateValue
          ? this.commonService.formatDateString(dateValue)
          : '';
          if(localStorage.getItem('country')=='/iam'){
            productNumber = productNumber?.toString().trim().replace(/\s+/g, '').substring(0, 10).toUpperCase();
          }
        const product: Product = {
          inputMaterialNumber: productNumber,
          expectedDate: formattedDate,
          quantity: +control.quantity || 1,
          isNonBoschPartNo: this.isOesCountry() ? control.isNonBoschPartNo || 0 : 0,
        };
        products.push(product);
      }
    });
    if(products.length>500){
      this.message.create('warning',  this.translate.instant('ADD_MAX_NUM_TIPS'));
      return;
    }
    this.cartService.addToCart(products).then(() => {
      const currentUrl = this.router.url;
      const country = localStorage.getItem("country"); 
      if (currentUrl !== `${country}/cart`) {
        this.router.navigateByUrl(`${country}/cart`);
      } 
      this.closeModal.emit();
    });
  }

  private validateAndProcessData(data: any[]): {
    isValid: boolean;
    data?: any[];
    errors?: string[];
  } {
    const validData: any[] = [];
    const errors: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    data.forEach((row, index) => {
      const materialNumber = row.materialNumber?.toString().trim() || '';
      const quantity = row.quantity ? parseInt(row.quantity) : 1;
      const expectedDate =
        row.expectedDate || this.commonService.formatDateString(new Date());
      const nonBoschPartNoFlag = row.nonBoschPartNoFlag?.toString().trim() || '';
      const isOes = this.isOesCountry();
      let isNonBoschPartNo = 0;
      let isNonBoschPartNoValid = true;

      if (isOes) {
        if (nonBoschPartNoFlag.toUpperCase() === 'Y') {
          isNonBoschPartNo = 1;
        } else if (nonBoschPartNoFlag !== '') {
          isNonBoschPartNoValid = false;
          this.translate.get('NON_BOSCH_FLAG_INVALID', { row: index + 1 })
            .subscribe((message: string) => errors.push(message));
        }
      }

      // validate material number
      if (materialNumber.length <= 1) {
        this.translate.get('INVALID_MATERIAL', { row: index + 1, materialNumber })
          .subscribe((message: string) => errors.push(message));
      }
      const validFormats = ['YYYY-MM-DD', 'MM/DD/YY', 'DD-MM-YYYY'];

      if (expectedDate) {
       if ( !moment(expectedDate, validFormats,false).isValid()) {
          this.translate.get('INVALID_DATE', { row: index + 1, date: expectedDate })
            .subscribe((message: string) => errors.push(message));
        }else if (new Date(expectedDate) < today) {
          this.translate.get('DATE_CANNOT_BE_BEFORE_TODAY', { row: index + 1, date: expectedDate })
            .subscribe((message: string) => errors.push(message));
        }
      }

      const isExpectedDateValid = !expectedDate || (moment(expectedDate, validFormats,false).isValid() && new Date(expectedDate) >= today);
      if (materialNumber.length > 1 && isExpectedDateValid && isNonBoschPartNoValid) {
        validData.push({ materialNumber, quantity, expectedDate, isNonBoschPartNo});
      }
    });
    return { isValid: errors.length === 0, data: validData, errors };
  }

  private isOesCountry(): boolean {
    const currentCountry = (localStorage.getItem('country') || '').trim().toLowerCase();
    const pathName = window.location.pathname.toLowerCase();
    return currentCountry === '/oes' || currentCountry === 'oes' || pathName.includes('/oes');
  }

  downloadCSV(path: string): void {
    const link = document.createElement('a');
    link.href = `/assets/${path}`;
    link.download = path;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
